import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, ShieldCheck, Phone, Mail, MapPin } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await login(email.trim(), password);
    setBusy(false);
    if (r.ok) {
      const from = location.state?.from;
      const dest = from ? `${from.pathname || "/"}${from.search || ""}${from.hash || ""}` : "/";
      nav(dest, { replace: true });
    } else setErr(r.error);
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-[1.1fr_440px]" data-testid="login-page">
      {/* Left — heavy brand panel (dark, industrial weight, real logo) */}
      <div
        className="hidden lg:flex relative overflow-hidden text-white"
        style={{ background: "#0E0E18" }}
      >
        {/* faint diagonal pattern + red gradient corner = industrial */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{
               backgroundImage: "linear-gradient(135deg, rgba(255,255,255,0.18) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.18) 75%, transparent 75%, transparent)",
               backgroundSize: "12px 12px",
             }} />
        <div className="absolute -right-32 -top-32 w-[420px] h-[420px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(230,20,50,0.22), transparent 60%)" }} />
        <div className="absolute -left-24 -bottom-24 w-[360px] h-[360px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(70,40,140,0.22), transparent 60%)" }} />

        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 flex">
          <div className="flex-1" style={{ background: "var(--accent)" }} />
          <div className="flex-1" style={{ background: "var(--brand)" }} />
          <div className="flex-1" style={{ background: "var(--tag)" }} />
        </div>

        <div className="relative p-14 flex flex-col justify-between w-full">
          {/* Logo card (V logo prints on dark, so use white tile) */}
          <div className="rounded-md p-6 inline-block self-start" style={{ background: "#fff", maxWidth: 340 }}>
            <img
              src="/brand/mr-logo-h.jpg"
              alt="Manchester Roofing Australia"
              style={{ width: "100%", height: "auto", display: "block" }}
              data-testid="login-logo"
            />
          </div>

          <div className="max-w-md">
            <div className="flex items-center gap-2 mb-4">
              <span className="h-0.5 w-8" style={{ background: "var(--accent)" }} />
              <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-white/80">
                Operations Portal
              </span>
            </div>
            <h1 className="wm text-[42px] leading-[1.05] font-extrabold" style={{ color: "#fff" }}>
              The metal roofing<br/>
              specialists.<br/>
              <span style={{ color: "var(--accent)" }}>Brisbane &amp; surrounds.</span>
            </h1>
            <p className="mt-5 text-[14px] leading-relaxed text-white/75 max-w-sm">
              Manage enquiries, quotes, jobs, documents and workflow from one secure place
              built for the way Manchester Roofing actually operates.
            </p>

            <div className="mt-7 grid grid-cols-2 gap-3 max-w-md">
              <Trust label="QBCC-licensed" />
              <Trust label="Bluescope-certified" />
              <Trust label="Commercial &amp; residential" />
              <Trust label="30+ years trusted" />
            </div>
          </div>

          {/* Contact strip */}
          <div className="grid grid-cols-3 gap-6 text-[12px] text-white/70 pt-6" style={{ borderTop: "1px solid rgba(255,255,255,0.10)" }}>
            <div className="flex items-center gap-2"><Phone size={13} className="text-white/50" /> 07 3490 8480</div>
            <div className="flex items-center gap-2"><Mail size={13} className="text-white/50" /> info@manchesterroofing.com.au</div>
            <div className="flex items-center gap-2"><MapPin size={13} className="text-white/50" /> Brendale, Brisbane</div>
          </div>
        </div>
      </div>

      {/* Right — sign-in */}
      <div className="flex items-center justify-center p-8 lg:p-14" style={{ background: "var(--bg-elev-1)" }}>
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden mb-7">
            <img src="/brand/mr-logo-h.jpg" alt="Manchester Roofing Australia" style={{ height: 48 }} />
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="h-0.5 w-6" style={{ background: "var(--accent)" }} />
            <span className="eyebrow" style={{ color: "var(--accent)" }}>Staff Sign-in</span>
          </div>
          <h2 className="wm text-[26px] font-extrabold mb-1" style={{ color: "var(--ink)" }}>Welcome back</h2>
          <p className="text-[13px] mb-7" style={{ color: "var(--ink-soft)" }}>
            Sign in to access the Manchester Roofing operations portal.
          </p>

          <label className="block text-[11px] font-semibold uppercase tracking-[0.10em] mb-1.5" style={{ color: "var(--ink-muted)" }}>Email</label>
          <input
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4"
            required
            autoFocus
          />

          <label className="block text-[11px] font-semibold uppercase tracking-[0.10em] mb-1.5" style={{ color: "var(--ink-muted)" }}>Password</label>
          <input
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-2"
            required
          />

          {err && (
            <div className="mt-2 text-[12px] font-semibold" style={{ color: "var(--bad)" }} data-testid="login-error">{err}</div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn btn-primary w-full mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ height: 44, fontSize: 14, fontWeight: 700 }}
            data-testid="login-submit"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : null}
            Sign in to portal
          </button>

          <div className="mt-7 flex items-center gap-2 text-[11px]" style={{ color: "var(--ink-muted)" }}>
            <ShieldCheck size={12} /> Restricted to authorised Manchester Roofing staff.
          </div>

          <div className="mt-10 pt-5 text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: "var(--ink-faint)", borderTop: "1px solid var(--line)" }}>
            © Manchester Roofing Australia
          </div>
        </form>
      </div>
    </div>
  );
}

function Trust({ label }) {
  return (
    <div className="flex items-center gap-2 text-[12px] text-white/85">
      <span className="w-1.5 h-1.5" style={{ background: "var(--accent)" }} />
      <span dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}
